import React, { useEffect, useRef, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { Row, Col, Image, Space, message } from 'antd'

import {
  PlusOutlined,
  SaveOutlined,
  PrinterOutlined,
  DeleteOutlined,
  CloseOutlined,
  QrcodeOutlined,
  DownloadOutlined
} from '@ant-design/icons'

// Import custom components
import Button from '../../components/common/button/button'
import Input from '../../components/common/input/input'
import Modal from '../../components/common/modal/modal'
import Table from '../../components/common/table/table'
import Spinner from '../../components/common/spinner/spinner'
import Typography from '../../components/common/typography/typography'
import VideoCapture from '../../components/barcodeScanner/BarcodeScanner'
import PrintPreview from '../../components/feature/printPreview/printPreview'

// Import assets
import noDataImage from '../../assets/images/noDataImage.png'
import savedImage from '../../assets/images/savedItem.png'
import a4Printer from '../../assets/images/a4Printer.png'
import labelPrinter from '../../assets/images/labelPrinter.png'

// Import utility functions
import {
  handleGenerateLabels,
  handleQuantityChange,
  handleSaveAPN,
  handleSaveRequest,
  handleBarcodeScan,
  exportMissingAPNs,
  handleDeleteRows,
  getSelectedApns,
  detectCognitoUser
} from '../shelvesEdgeLabelling/shelvesEdgeLabellingUtils'

// Import styles
import './shelvesEdgeLabelling.scss'

const { Title, Text } = Typography

const ShelvesEdgeLabelling = () => {
  // State declarations
  const [isReloading, setIsReloading] = useState(false)
  const [rows, setRows] = useState([])
  const [open, setOpen] = useState(false)
  const [apnInput, setApnInput] = useState('')
  const [error, setError] = useState('')
  const [editingRowId, setEditingRowId] = useState(null)
  const [printOpen, setPrintOpen] = useState(false)
  const [selectedPrinter, setSelectedPrinter] = useState('A4')
  const [selectedRows, setSelectedRows] = useState([])
  const [scannedApns, setScannedApns] = useState(new Set())
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [requestName, setRequestName] = useState('')
  const [userName, setUserName] = useState('')
  const [imageDialogOpen, setImageDialogOpen] = useState(false)
  const [productData, setProductData] = useState([])
  const [missingAPNs, setMissingAPNs] = useState([])
  const [missingAPNDialogOpen, setMissingAPNDialogOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isScannerActive, setIsScannerActive] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700)
  const [showPrintPreview, setShowPrintPreview] = useState(false)
  const [isCognitoUser, setIsCognitoUser] = useState(false)

  const printPreviewRef = useRef()
  const { instance } = useMsal()

  // Create a style element for print settings
  const pageStyle = `
    @media print {
      @page {
        size: ${selectedPrinter === 'Zebra' ? '7cm 4cm' : 'A4'};
        margin: 0;
      }
      body {
        -webkit-print-color-adjust: exact;
      }
      
      /* Hide everything except print content */
      body * {
        visibility: hidden;
      }
      
      .print-only-content,
      .print-only-content * {
        visibility: visible !important;
      }
      
      /* Position the print content */
      .print-only-content {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
      }
    }
  `

  // Detect authentication type
  useEffect(() => {
    const checkAuthType = async () => {
      try {
        const cognitoUser = await detectCognitoUser(instance)
        setIsCognitoUser(cognitoUser)
      } catch (error) {
        console.error('Error detecting auth type:', error)
        setIsCognitoUser(false)
      }
    }

    checkAuthType()
  }, [instance])

  // Check for mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 700)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Add and remove print style when printer type changes
  useEffect(() => {
    // Create or update the style element
    let styleElement = document.getElementById('print-style-element')

    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = 'print-style-element'
      document.head.appendChild(styleElement)
    }

    styleElement.textContent = pageStyle

    return () => {
      if (styleElement) {
        document.head.removeChild(styleElement)
      }
    }
  }, [pageStyle, selectedPrinter])

  // Table columns configuration
  const columns = [
    {
      title: 'APN',
      dataIndex: 'apn',
      key: 'apn'
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (text, record) =>
        editingRowId === record.id ? (
          <Input
            type="number"
            min={1}
            value={text}
            onChange={(e) =>
              handleQuantityChange(record.id, parseInt(e.target.value), setRows)
            }
            onBlur={() => setEditingRowId(null)}
            className="quantity-input"
          />
        ) : (
          <div onDoubleClick={() => setEditingRowId(record.id)}>{text}</div>
        )
    }
  ]

  // Generate labels and handle print flow
  const onGenerateLabels = async () => {
    try {
      setIsReloading(true)

      // Call the utility function to generate labels and pass the authentication type
      const result = await handleGenerateLabels(
        instance,
        setIsReloading,
        rows,
        selectedRows,
        setProductData,
        setMissingAPNs,
        setPrintOpen,
        isCognitoUser
      )

      if (!result) {
        message.error('Failed to generate labels. Please provide valid APN list')
      }
    } catch (error) {
      console.error('Error generating labels:', error)
      message.error('Failed to generate labels')
    } finally {
      setIsReloading(false)
    }
  }

  const onSaveAPN = () => {
    handleSaveAPN(apnInput, setError, setRows, setOpen, setApnInput)
  }

  const onSaveRequest = () => {
    handleSaveRequest(
      requestName,
      userName,
      rows,
      instance,
      setLoading,
      setSaveDialogOpen,
      setRequestName,
      setImageDialogOpen,
      isCognitoUser
    )
  }

  const onBarcodeScan = (barcodeResult) => {
    handleBarcodeScan(
      barcodeResult,
      scannedApns,
      setRows,
      setScannedApns,
      setIsScannerActive
    )
  }

  const onExportMissingAPNs = () => {
    exportMissingAPNs(missingAPNs)
  }

  const onDeleteRows = () => {
    handleDeleteRows(selectedRows, setRows, setSelectedRows)
  }

  const handlePrintSelection = () => {
    setPrintOpen(false)
    setShowPrintPreview(true)
    setTimeout(() => {
      const styleElement = document.createElement('style')
      styleElement.id = 'print-fixes-style'

      // Add critical print styles that enable multi-page printing
      styleElement.textContent = `
      @media print {
        @page {
          size: ${selectedPrinter === 'Zebra' ? '7cm 4cm' : 'A4'};
          margin: 0;
        }
        
        html, body {
          height: auto !important;
          overflow: visible !important;
        }
        
        .print-preview__A4container {
          page-break-after: always !important;
          page-break-inside: avoid !important;
        }
        
        .print-preview__Zebra-container {
          page-break-after: always !important;
          page-break-inside: avoid !important;
        }
      }
    `

      // Add the style element to the head
      document.head.appendChild(styleElement)

      // Directly trigger the browser's print dialog
      window.print()

      // Clean up after printing is done
      setTimeout(() => {
        // Remove the temporary style element
        if (document.getElementById('print-fixes-style')) {
          document.head.removeChild(document.getElementById('print-fixes-style'))
        }
        setShowPrintPreview(false)
      }, 500)
    }, 300)
  }

  return (
    <main >
      {!isScannerActive && (
        <>
          <Title level={4} className="shelves-edge-title">
            Shelf Edge Labels
          </Title>
          <hr></hr>
        </>
      )}
      <Row
        justify="space-between"
        align="middle"
        className="shelves-edge-action-row"
      >
        {!isScannerActive && (
          <>
            <Col>
              <Space>
                {rows.length > 0 && (
                  <>
                    <Button
                      icon={<SaveOutlined />}
                      onClick={() => setSaveDialogOpen(true)}
                    >
                      {!isMobile && 'Save for later'}
                    </Button>
                    <Button
                      icon={<PrinterOutlined />}
                      disabled={selectedRows.length === 0}
                      onClick={onGenerateLabels}
                    >
                      {!isMobile && 'Print'}
                    </Button>
                    <Button
                      icon={<DeleteOutlined />}
                      danger
                      disabled={selectedRows.length === 0}
                      onClick={onDeleteRows}
                    >
                      {!isMobile && 'Delete'}
                    </Button>
                  </>
                )}
              </Space>
            </Col>
            <Col>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setOpen(true)}
                >
                  {!isMobile && 'Add APN'}
                </Button>
                {isMobile && (
                  <Button
                    type="primary"
                    icon={<QrcodeOutlined />}
                    onClick={() => setIsScannerActive(true)}
                  >
                    {!isMobile && 'Scan Barcode'}
                  </Button>
                )}
              </Space>
            </Col>
          </>
        )}
      </Row>

      {isScannerActive ? (
        <div className="scanner-section">
          <div className="scanner-header">
            <Button
              type="primary"
              danger
              icon={<CloseOutlined />}
              onClick={() => setIsScannerActive(false)}
              className="scanner-cancel-btn"
            >
              Cancel
            </Button>
          </div>
          <div className="scanner-container-embedded">
            <VideoCapture onBarcodeScanned={onBarcodeScan} />
          </div>
        </div>
      ) : (
        rows.length > 0 ? (
          <div style={{ position: 'relative', minHeight: '400px' }}>
            {isReloading && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Spinner style={{ transform: 'scale(1.5)' }} />
              </div>
            )}
            <Table
              dataSource={rows}
              columns={columns}
              rowKey="id"
              rowSelection={{
                selectedRowKeys: selectedRows,
                onChange: setSelectedRows
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: false
              }}
              className="shelves-edge-table"
              style={{ opacity: isReloading ? 0.5 : 1 }}
            />
          </div>
        ) : (
          <div className="shelves-edge-no-data">
            <Image
              src={noDataImage}
              alt="No Data"
              preview={false}
              className="shelves-edge-no-data-image"
            />
          </div>
        )
      )}

      {/* Add APN Modal */}
      <Modal
        title="Add APN"
        open={open}
        onOk={onSaveAPN}
        onCancel={() => {
          setOpen(false)
          setError('')
          setApnInput('')
        }}
        okText="Add"
        cancelText="Close"
        className="shelves-edge-modal"
      >
        <Input
          type="textarea"
          placeholder="Enter 13 digit APN(s)"
          value={apnInput}
          onChange={(e) => {
            let sanitizedInput = e.target.value.replace(/[\r\n]+/g, ',')
            sanitizedInput = sanitizedInput.replace(/[^0-9, ]/g, '')
            setApnInput(sanitizedInput)
          }}
          rows={4}
          status={error ? 'error' : ''}
          className="shelves-edge-apn-input"
        />
        {error && (
          <Text type="danger" className="error-text">
            {error}
          </Text>
        )}
      </Modal>

      {/* Save Request Modal */}
      <Modal
        title="Save Request"
        open={saveDialogOpen}
        onOk={onSaveRequest}
        onCancel={() => {
          setSaveDialogOpen(false)
          setRequestName('')
          setUserName('')
        }}
        confirmLoading={loading}
        className="shelves-edge-modal"
      >
        <Space direction="vertical" size="middle">
          <Input
            placeholder="Request Name"
            value={requestName}
            onChange={(e) => setRequestName(e.target.value)}
            required
            className="shelves-edge-input"
          />
          <Input
            placeholder="User Name"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            required
            className="shelves-edge-input"
          />
        </Space>
      </Modal>

      {/* Print Selection Modal */}
      <Modal
        title="Select Printer"
        open={printOpen}
        onCancel={() => setPrintOpen(false)}
        footer={[
          <Button
            key="print"
            type="primary"
            disabled={!selectedPrinter}
            onClick={handlePrintSelection}
          >
            Confirm & Print
          </Button>
        ]}
        className="shelves-edge-modal"
      >
        <Row justify="space-around" align="middle">
          <Col span={11}>
            <div
              className={`printer-option ${selectedPrinter === 'A4' ? 'selected' : ''}`}
              onClick={() => setSelectedPrinter('A4')}
            >
              <Image src={a4Printer} alt="A4 Printer" width={100} preview={false} />
              <p>A4 Printer</p>
            </div>
          </Col>
          <Col span={11}>
            <div
              className={`printer-option ${selectedPrinter === 'Zebra' ? 'selected' : ''}`}
              onClick={() => setSelectedPrinter('Zebra')}
            >
              <Image src={labelPrinter} alt="Zebra Printer" width={100} preview={false} />
              <p>Zebra Printer</p>
            </div>
          </Col>
        </Row>

        {missingAPNs.length > 0 && (
          <Row justify="start" className="invalid-apn-row">
            <Button
              type="link"
              danger
              onClick={() => setMissingAPNDialogOpen(true)}
            >
              View invalid APN list
            </Button>
          </Row>
        )}
      </Modal>

      {/* Missing APNs Modal */}
      <Modal
        title={
          <div className="shelves-edge-missing-header">
            <span>Invalid APNs</span>
            <Space>
              <Button icon={<DownloadOutlined />} onClick={onExportMissingAPNs}>
                Download
              </Button>
            </Space>
          </div>
        }
        open={missingAPNDialogOpen}
        onCancel={() => setMissingAPNDialogOpen(false)}
        footer={null}
        className="shelves-edge-modal"
      >
        <Table
          dataSource={missingAPNs.map((apn, index) => ({
            key: index,
            id: index + 1,
            apn: apn
          }))}
          columns={[
            { title: 'No.', dataIndex: 'id', width: 100 },
            { title: 'Invalid APN', dataIndex: 'apn', flex: 1 }
          ]}
          pagination={false}
          scroll={{ y: 300 }}
          className="invalid-apn-table"
          rowKey="key"
        />
      </Modal>

      {/* Success Modal */}
      <Modal
        open={imageDialogOpen}
        onCancel={() => {
          setImageDialogOpen(false)
          setRows([])
        }}
        footer={[
          <Button
            key="back"
            type="primary"
            onClick={() => {
              setImageDialogOpen(false)
              setRows([])
            }}
          >
            OK
          </Button>
        ]}
        className="shelves-edge-success-modal"
      >
        <div className="shelves-edge-success-content">
          <Image src={savedImage} alt="Saved Successfully" preview={false} />
          <Title level={2}>Thank You</Title>
          <Text>Shelf Edge report saved successfully.</Text>
        </div>
      </Modal>

      {/* Hidden Print Content - only shows during actual printing */}
      {showPrintPreview && (
        <div className="print-only-content">
          <PrintPreview
            ref={printPreviewRef}
            products={productData}
            printList={getSelectedApns(rows, selectedRows)}
            selectedPrinter={selectedPrinter}
          />
        </div>
      )}
    </main>
  )
}

export default ShelvesEdgeLabelling